import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Copy, Eye, EyeOff, Loader2, Mail, Phone, ShieldCheck, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LP_CSS } from "@/pages/Landing";

/* Public demo signup: 1) your details → 2) verify email + phone (OTP) → 3) demo login details.
   No account is created for the visitor; they receive the shared demo workspace login. */

const COUNTRY_CODES = [
  ["IN", "+91"], ["US", "+1"], ["GB", "+44"], ["AE", "+971"], ["SG", "+65"], ["AU", "+61"], ["CA", "+1"], ["DE", "+49"],
  ["SA", "+966"], ["QA", "+974"], ["ZA", "+27"], ["NZ", "+64"], ["MY", "+60"], ["ID", "+62"], ["PH", "+63"], ["NG", "+234"],
  ["KE", "+254"], ["FR", "+33"], ["NL", "+31"], ["IE", "+353"], ["CH", "+41"], ["SE", "+46"],
];
const SIZES = ["1–10", "11–50", "51–200", "201–500", "500+"];
const PLAN_LABEL = { starter: "Starter", growth: "Growth", enterprise: "Enterprise" };

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-semibold mb-1.5" style={{ color: "var(--ink2)" }}>{label}</span>
      {children}
      {hint && <span className="block text-[11px] mt-1" style={{ color: "var(--mute)" }}>{hint}</span>}
    </label>
  );
}
const inputCls = "w-full rounded-xl border bg-white px-3.5 py-2.5 text-[15px] outline-none transition focus:border-[#7C3AED] focus:ring-4 focus:ring-[#7C3AED]/10";
const inputStyle = { borderColor: "var(--line)", color: "var(--ink)" };

function Stepper({ step }) {
  const items = ["Your details", "Verify", "Demo access"];
  return (
    <ol className="flex items-center justify-center gap-2 sm:gap-3 mb-8" data-testid="demo-stepper" aria-label="Progress">
      {items.map((t, i) => {
        const n = i + 1, done = step > n, cur = step === n;
        return (
          <li key={t} className="flex items-center gap-2 sm:gap-3">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${done || cur ? "text-white lp-grad" : ""}`}
              style={!(done || cur) ? { background: "#EEF0FA", color: "var(--mute)" } : undefined} data-testid={`demo-step-${n}`} aria-current={cur ? "step" : undefined}>
              {done ? <Check className="w-4 h-4" strokeWidth={3} /> : n}
            </span>
            <span className={`text-sm font-semibold hidden sm:block ${cur ? "" : "opacity-60"}`}>{t}</span>
            {i < items.length - 1 && <span className="w-6 sm:w-12 h-px" style={{ background: step > n ? "#7C3AED" : "var(--line)" }} />}
          </li>
        );
      })}
    </ol>
  );
}

/** Six single-digit boxes with auto-advance, backspace and paste support. */
function OtpInput({ value, onChange, testid, disabled }) {
  const refs = useRef([]);
  const digits = (value + "      ").slice(0, 6).split("");
  const setAt = (i, ch) => {
    const arr = (value + "      ").slice(0, 6).split("");
    arr[i] = ch;
    onChange(arr.join("").replace(/ /g, ""));
  };
  return (
    <div className="flex gap-2" data-testid={testid}>
      {digits.map((d, i) => (
        <input key={i} ref={(el) => (refs.current[i] = el)} inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"} maxLength={6}
          value={d.trim()} disabled={disabled} aria-label={`Digit ${i + 1}`} data-testid={`${testid}-${i}`}
          className="w-11 h-12 sm:w-12 sm:h-13 text-center text-xl font-semibold rounded-xl border bg-white outline-none transition focus:border-[#7C3AED] focus:ring-4 focus:ring-[#7C3AED]/10"
          style={{ borderColor: "var(--line)", color: "var(--ink)" }}
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, "");
            if (raw.length > 1) {
              // whole code typed/auto-filled (e.g. SMS autofill) into one box → spread it across the boxes
              const arr = (value + "      ").slice(0, 6).split("");
              raw.split("").slice(0, 6 - i).forEach((c, k) => { arr[i + k] = c; });
              onChange(arr.join("").replace(/ /g, ""));
              refs.current[Math.min(i + raw.length, 5)]?.focus();
              return;
            }
            // single digit (or a deletion); `slice(-1)` keeps the newest digit if a filled box is typed over
            const ch = raw.slice(-1);
            setAt(i, ch);
            if (ch && i < 5) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !d.trim() && i > 0) { refs.current[i - 1]?.focus(); setAt(i - 1, ""); }
            if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
            if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => {
            const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
            if (t) { e.preventDefault(); onChange(t); refs.current[Math.min(t.length, 5)]?.focus(); }
          }} />
      ))}
    </div>
  );
}

function Countdown({ seconds, onResend, busy, testid }) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => { setLeft(seconds); }, [seconds]);
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return left > 0
    ? <span className="text-xs" style={{ color: "var(--mute)" }}>Resend in {left}s</span>
    : <button type="button" onClick={onResend} disabled={busy} data-testid={testid} className="text-xs font-semibold hover:underline" style={{ color: "var(--violet)" }}>{busy ? "Sending…" : "Resend code"}</button>;
}

export default function DemoSignup() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const plan = (params.get("plan") || "").toLowerCase();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", cc: "+91", phone: "", company: "", job_title: "", company_size: "", country: "", message: "" });
  const [lead, setLead] = useState(null);                       // { lead_id, email_masked, phone_masked, dev_otps? }
  const [codes, setCodes] = useState({ email: "", phone: "" });
  const [fieldErr, setFieldErr] = useState({});
  const [resendKey, setResendKey] = useState({ email: 0, phone: 0 });
  const [resending, setResending] = useState(null);
  const [demo, setDemo] = useState(null);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "Try the PRIZM live demo";
    return () => { document.title = prev; };
  }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fullPhone = useMemo(() => `${form.cc}${form.phone.replace(/\D/g, "")}`, [form.cc, form.phone]);

  const submitDetails = async (e) => {
    e.preventDefault();
    setError("");
    if (form.full_name.trim().length < 2) return setError("Please enter your full name.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return setError("Please enter a valid work email.");
    if (form.phone.replace(/\D/g, "").length < 6) return setError("Please enter a valid mobile number.");
    if (form.company.trim().length < 2) return setError("Please enter your company name.");
    setBusy(true);
    try {
      const { data } = await api.post("/demo/start", {
        full_name: form.full_name.trim(), email: form.email.trim(), phone: fullPhone, company: form.company.trim(),
        job_title: form.job_title || null, company_size: form.company_size || null, country: form.country || null,
        interested_plan: plan || null, message: form.message || null,
      });
      setLead(data); setCodes({ email: "", phone: "" }); setFieldErr({}); setStep(2);
    } catch (err) { setError(apiErrorMessage(err, "Something went wrong. Please try again.")); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setFieldErr({});
    if (codes.email.length !== 6 || codes.phone.length !== 6) return setError("Enter both 6-digit codes.");
    setBusy(true);
    try {
      const { data } = await api.post("/demo/verify", { lead_id: lead.lead_id, email_otp: codes.email, phone_otp: codes.phone });
      setDemo(data.demo); setStep(3);
    } catch (err) {
      // lib/api.js flattens `detail` to a string; the structured per-field errors are kept on _rawData
      const d = err?.response?._rawData?.detail;
      if (d && typeof d === "object" && d.errors) { setFieldErr(d.errors); setError(""); }
      else setError(apiErrorMessage(err, "Verification failed. Please try again."));
    } finally { setBusy(false); }
  };

  const resend = async (channel) => {
    setResending(channel); setError("");
    try {
      const { data } = await api.post("/demo/resend", { lead_id: lead.lead_id, channel });
      setResendKey((k) => ({ ...k, [channel]: k[channel] + 1 }));
      setCodes((c) => ({ ...c, [channel]: "" })); setFieldErr((f) => ({ ...f, [channel]: undefined }));
      if (data.dev_otp) setLead((l) => ({ ...l, dev_otps: { ...(l.dev_otps || {}), [channel]: data.dev_otp } }));
      toast.success(`A new code was sent to your ${channel}.`);
    } catch (err) { setError(apiErrorMessage(err, "Could not resend the code.")); }
    finally { setResending(null); }
  };

  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error("Copy failed — select and copy manually."); }
  };

  const openDemo = async () => {
    setBusy(true);
    try { await login(demo.email, demo.password); nav("/"); }
    catch (err) { toast.error(apiErrorMessage(err, "Couldn't sign in to the demo. Use the details above on the sign-in page.")); setBusy(false); }
  };

  return (
    <div className="lp min-h-screen relative overflow-hidden" data-testid="demo-page">
      <style>{LP_CSS}</style>
      <div className="absolute inset-0 -z-10" style={{ background: "var(--soft)" }} />
      <header className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/welcome" aria-label="PRIZM home"><img src="/prizm-logo-light.png" alt="PRIZM" className="h-9 w-auto" /></Link>
        <Link to="/welcome" className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--ink2)" }}><ArrowLeft className="w-4 h-4" />Back to site</Link>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 pb-16 pt-4 grid lg:grid-cols-5 gap-10 items-start">
        {/* Left: pitch */}
        <aside className="lg:col-span-2 lg:sticky lg:top-8 space-y-6">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold" style={{ borderColor: "var(--line)", background: "var(--soft)", color: "var(--ink2)" }}>
              <Sparkles className="w-3.5 h-3.5 text-[#E0409A]" />Live demo{plan && PLAN_LABEL[plan] ? ` · interested in ${PLAN_LABEL[plan]}` : ""}
            </span>
            <h1 className="mt-4 text-3xl sm:text-4xl font-semibold leading-tight" data-testid="demo-title">
              See PRIZM <span className="lp-grad-text">in action</span>
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "var(--ink2)" }}>
              Tell us a little about you, verify your email and phone, and get instant access to our demo workspace — with the dashboard, projects, people, AI and chat to explore.
            </p>
          </div>
          <ul className="space-y-3">
            {[["Dedicated deployment per company", "Your real PRIZM runs on its own app, database and storage."], ["No shared database", "Nothing is shared between companies."], ["Handled by our team", "We set up, onboard and manage your subscription directly."]].map(([t, b]) => (
              <li key={t} className="flex gap-3">
                <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full lp-grad flex items-center justify-center"><Check className="w-3.5 h-3.5 text-white" strokeWidth={3} /></span>
                <div><div className="text-sm font-semibold">{t}</div><div className="text-[13px]" style={{ color: "var(--ink2)" }}>{b}</div></div>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--mute)" }}><ShieldCheck className="w-4 h-4 text-[#1fbf8a]" />We only use your details to share the demo and follow up about PRIZM.</div>
        </aside>

        {/* Right: wizard */}
        <section className="lg:col-span-3">
          <div className="rounded-3xl border bg-white p-6 sm:p-9 shadow-[0_24px_60px_-34px_rgba(17,27,58,.3)]" style={{ borderColor: "var(--line)" }}>
            <Stepper step={step} />

            {error && <div role="alert" className="mb-5 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#f5c2c7", background: "#fff5f5", color: "#b42335" }} data-testid="demo-error">{error}</div>}

            {step === 1 && (
              <form onSubmit={submitDetails} className="space-y-4" data-testid="demo-form-details" noValidate>
                <h2 className="text-xl font-semibold">Your details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name *"><input className={inputCls} style={inputStyle} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} autoComplete="name" data-testid="demo-name" /></Field>
                  <Field label="Work email *"><input type="email" className={inputCls} style={inputStyle} value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" data-testid="demo-email" /></Field>
                  <Field label="Mobile number *" hint="We'll text a code to this number.">
                    <div className="flex gap-2">
                      <select className={`${inputCls} !w-[96px] shrink-0`} style={inputStyle} value={form.cc} onChange={(e) => set("cc", e.target.value)} aria-label="Country code" data-testid="demo-cc">
                        {COUNTRY_CODES.map(([c, code], i) => <option key={`${c}${i}`} value={code}>{c} {code}</option>)}
                      </select>
                      <input type="tel" inputMode="tel" className={inputCls} style={inputStyle} value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel-national" placeholder="98765 43210" data-testid="demo-phone" />
                    </div>
                  </Field>
                  <Field label="Company *"><input className={inputCls} style={inputStyle} value={form.company} onChange={(e) => set("company", e.target.value)} autoComplete="organization" data-testid="demo-company" /></Field>
                  <Field label="Your role"><input className={inputCls} style={inputStyle} value={form.job_title} onChange={(e) => set("job_title", e.target.value)} placeholder="e.g. Founder, HR Head" autoComplete="organization-title" /></Field>
                  <Field label="Company size">
                    <select className={inputCls} style={inputStyle} value={form.company_size} onChange={(e) => set("company_size", e.target.value)}>
                      <option value="">Select…</option>{SIZES.map((s) => <option key={s} value={s}>{s} employees</option>)}
                    </select>
                  </Field>
                  <Field label="Country"><input className={inputCls} style={inputStyle} value={form.country} onChange={(e) => set("country", e.target.value)} autoComplete="country-name" /></Field>
                </div>
                <Field label="Anything we should know? (optional)"><textarea rows={3} className={inputCls} style={inputStyle} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Number of branches, tools you use today…" /></Field>
                <button type="submit" disabled={busy} className="lp-btn lp-btn-primary w-full justify-center !py-3.5 disabled:opacity-70" data-testid="demo-continue">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Sending codes…</> : <>Continue &amp; verify <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}

            {step === 2 && lead && (
              <form onSubmit={verify} className="space-y-6" data-testid="demo-form-verify">
                <div>
                  <h2 className="text-xl font-semibold">Verify your email &amp; phone</h2>
                  <p className="text-sm mt-1" style={{ color: "var(--ink2)" }}>We sent a 6-digit code to each. Enter both to continue — they expire in 10 minutes.</p>
                </div>
                {[["email", Mail, lead.email_masked, "Email code"], ["phone", Phone, lead.phone_masked, "SMS code"]].map(([ch, Icon, masked, label]) => (
                  <div key={ch} className="rounded-2xl border p-4 sm:p-5" style={{ borderColor: fieldErr[ch] ? "#f5c2c7" : "var(--line)", background: "var(--soft)" }} data-testid={`demo-verify-${ch}`}>
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-9 h-9 rounded-xl lp-grad flex items-center justify-center text-white shrink-0"><Icon className="w-4 h-4" /></span>
                        <div className="min-w-0"><div className="text-sm font-semibold">{label}</div><div className="text-xs truncate" style={{ color: "var(--mute)" }}>Sent to {masked}</div></div>
                      </div>
                      <Countdown key={resendKey[ch]} seconds={lead.resend_in || 30} busy={resending === ch} onResend={() => resend(ch)} testid={`demo-resend-${ch}`} />
                    </div>
                    <OtpInput value={codes[ch]} onChange={(v) => { setCodes((c) => ({ ...c, [ch]: v })); setFieldErr((f) => ({ ...f, [ch]: undefined })); }} testid={`demo-otp-${ch}`} disabled={busy} />
                    {fieldErr[ch] && <div className="mt-2 text-xs font-medium" style={{ color: "#b42335" }} data-testid={`demo-otp-error-${ch}`}>{fieldErr[ch]}</div>}
                  </div>
                ))}
                {lead.dev_otps && (
                  <div className="rounded-xl border border-dashed px-4 py-2.5 text-xs" style={{ borderColor: "#F5B83D", background: "#FFFBEB", color: "#92400E" }} data-testid="demo-dev-otps">
                    Dev mode — codes: email <b>{lead.dev_otps.email}</b> · phone <b>{lead.dev_otps.phone}</b>
                  </div>
                )}
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setStep(1); setError(""); }} className="lp-btn lp-btn-ghost" disabled={busy}><ArrowLeft className="w-4 h-4" />Edit details</button>
                  <button type="submit" disabled={busy || codes.email.length !== 6 || codes.phone.length !== 6} className="lp-btn lp-btn-primary flex-1 justify-center disabled:opacity-60" data-testid="demo-verify-btn">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Verifying…</> : <>Verify &amp; get demo access <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              </form>
            )}

            {step === 3 && demo && (
              <div className="space-y-6" data-testid="demo-success">
                <div className="text-center">
                  <span className="mx-auto w-14 h-14 rounded-full lp-grad flex items-center justify-center"><Check className="w-7 h-7 text-white" strokeWidth={3} /></span>
                  <h2 className="mt-4 text-2xl font-semibold">You're verified, {form.full_name.split(" ")[0]}!</h2>
                  <p className="text-sm mt-1" style={{ color: "var(--ink2)" }}>Here are the login details for the PRIZM demo workspace.</p>
                </div>
                <div className="rounded-2xl border p-5 space-y-4" style={{ borderColor: "var(--line)", background: "var(--soft)" }} data-testid="demo-credentials">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--mute)" }}>Email / username</div>
                    <div className="mt-1 flex items-center justify-between gap-2 rounded-xl bg-white border px-3.5 py-2.5" style={{ borderColor: "var(--line)" }}>
                      <span className="font-mono text-sm break-all" data-testid="demo-cred-email">{demo.email}</span>
                      <button type="button" onClick={() => copy(demo.email, "Email")} aria-label="Copy email" className="p-1.5 rounded-lg hover:bg-[#EEF0FA]"><Copy className="w-4 h-4" style={{ color: "var(--violet)" }} /></button>
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--mute)" }}>Password</div>
                    <div className="mt-1 flex items-center justify-between gap-2 rounded-xl bg-white border px-3.5 py-2.5" style={{ borderColor: "var(--line)" }}>
                      <span className="font-mono text-sm break-all" data-testid="demo-cred-password">{showPw ? demo.password : "•".repeat(Math.min(demo.password.length, 14))}</span>
                      <span className="flex gap-1 shrink-0">
                        <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="p-1.5 rounded-lg hover:bg-[#EEF0FA]">{showPw ? <EyeOff className="w-4 h-4" style={{ color: "var(--violet)" }} /> : <Eye className="w-4 h-4" style={{ color: "var(--violet)" }} />}</button>
                        <button type="button" onClick={() => copy(demo.password, "Password")} aria-label="Copy password" className="p-1.5 rounded-lg hover:bg-[#EEF0FA]"><Copy className="w-4 h-4" style={{ color: "var(--violet)" }} /></button>
                      </span>
                    </div>
                  </div>
                </div>
                <button type="button" onClick={openDemo} disabled={busy} className="lp-btn lp-btn-primary w-full justify-center !py-3.5 disabled:opacity-70" data-testid="demo-open">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Signing in…</> : <>Open the demo now <ArrowRight className="w-4 h-4" /></>}
                </button>
                <div className="flex gap-2.5 rounded-xl border px-4 py-3 text-[13px]" style={{ borderColor: "var(--line)", color: "var(--ink2)" }}>
                  <Lock className="w-4 h-4 shrink-0 mt-0.5 text-[#F26B3A]" />
                  <span>This is a shared demo workspace — other visitors use it too and its data is reset regularly, so please don't enter confidential information. Our team will reach out on <b>{lead?.email_masked}</b> to talk about your own dedicated deployment.</span>
                </div>
                <div className="text-center"><Link to="/welcome" className="text-sm font-semibold" style={{ color: "var(--violet)" }}>Back to the PRIZM site</Link></div>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
